import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { achievementFragment } from '../../client/fragments/achievement';
import { AchievementFieldsFragment, GetSingleAchievementQuery, GetSingleAchievementQueryVariables } from '../../client/generated/graphql';
import { getSingleAchievementQuery } from '../../client/queries/singleAchievement';

export const useCachedAchievementWithFallback = (
    achievementId?: string | null,
): {
    achievement: AchievementFieldsFragment | null;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const client = useApolloClient();

    // Try to read from normalized cache first
    const cachedAchievement = achievementId
        ? client.cache.readFragment<AchievementFieldsFragment>({
              id: client.cache.identify({ __typename: 'Achievement', id: achievementId }),
              fragment: achievementFragment,
              fragmentName: 'AchievementFields',
          })
        : null;

    // If not in cache, fetch from network
    const {
        data: networkData,
        loading,
        error,
    } = useQuery<GetSingleAchievementQuery, GetSingleAchievementQueryVariables>(getSingleAchievementQuery, {
        variables: { id: achievementId! },
        skip: !achievementId || !!cachedAchievement,
        fetchPolicy: 'cache-first',
    });

    if (!achievementId) {
        return { achievement: null, loading: false, error: undefined };
    }

    const achievement = cachedAchievement ?? networkData?.achievement ?? null;

    return {
        achievement,
        loading: loading && !cachedAchievement,
        error,
    };
};
